/**
 * The "Service calls" section of the business details (D86): a fee covering the
 * first minutes and km, labour per 15 minutes after, travel past the included
 * km. Controlled — the business form owns the draft and the one Save button.
 */
import { money } from "../lib/format";
import {
  type ServiceText,
  EXAMPLE_KM_ONE_WAY,
  EXAMPLE_MINUTES,
  exampleCents,
  serviceInvalid,
} from "../lib/servicerates";

type Box = { key: keyof ServiceText; label: string; hint?: string; inputMode: "decimal" | "numeric" };

const BOXES: Box[] = [
  { key: "fee", label: "Service call fee ($)", hint: "Covers the first minutes and km below.", inputMode: "decimal" },
  { key: "minutes", label: "Minutes included", hint: "On site, before labour starts.", inputMode: "numeric" },
  {
    key: "hourly",
    label: "Hourly rate ($)",
    hint: "Charged per 15 minutes after, to the nearest block.",
    inputMode: "decimal",
  },
  { key: "km", label: "Km included", hint: "Round trip, before travel is charged.", inputMode: "numeric" },
  { key: "kmRate", label: "Km rate ($/km)", hint: "e.g. 1.10", inputMode: "decimal" },
];

export function ServiceCallSettings({
  text,
  saved,
  canEdit,
  onChange,
}: {
  text: ServiceText;
  saved: ServiceText;
  canEdit: boolean;
  onChange: (key: keyof ServiceText, value: string) => void;
}) {
  const bad = serviceInvalid(text);
  const example = exampleCents(text, saved);
  return (
    <div>
      <h3 className="sub-head">Service calls</h3>
      <div className="form-grid">
        {BOXES.map((b) => (
          <label key={b.key} className="field">
            <span>{b.label}</span>
            <input
              inputMode={b.inputMode}
              value={text[b.key]}
              disabled={!canEdit}
              aria-invalid={bad.includes(b.key) || undefined}
              onChange={(e) => onChange(b.key, e.target.value)}
            />
            {b.hint && <small className="tiny muted">{b.hint}</small>}
          </label>
        ))}
      </div>
      {bad.length > 0 && (
        <p className="notice notice-warn" role="status" style={{ marginTop: 8 }}>
          Dollar boxes take a number like 120 or 1.10; minutes and km are whole numbers.
        </p>
      )}
      <p className="tiny" style={{ marginTop: 8 }} data-testid="service-example">
        {example === null ? (
          <span className="muted">Set a fee or an hourly rate to see an example.</span>
        ) : (
          <>
            e.g. {EXAMPLE_MINUTES / 60} hr on site, {EXAMPLE_KM_ONE_WAY} km away:{" "}
            <b className="mono">{money(example)}</b> <span className="muted">(ex GST)</span>
          </>
        )}
      </p>
    </div>
  );
}
